import { useEffect, useState } from 'react'

/**
 * A value that only updates once it has stopped changing for `ms`.
 *
 * For work that costs something per change — a round trip, a render of somebody
 * else's document. Never for a filter over data already in hand: there is
 * nothing to coalesce there, and the delay is all the reader gets.
 *
 * **Give it a primitive or a memoised value.** Handed an object literal it is
 * the RENDER that is debounced rather than the value: a fresh object every
 * render is a fresh dependency every render, so the timer restarts whenever
 * anything at all redraws the page, and the value settles only once the page
 * goes quiet. That happens to look right while the renders are the typing it is
 * waiting for, and stops being right the moment something else redraws.
 */
export function useDebounced<T>(value: T, ms: number): T {
  const [settled, setSettled] = useState(value)
  useEffect(() => {
    const t = window.setTimeout(() => setSettled(value), ms)
    return () => window.clearTimeout(t)
  }, [value, ms])
  return settled
}
